import { BaseProvider } from '@ethersproject/providers'
import { Protocol } from '@uniswap/router-sdk'
import { Currency, Fraction, Token, TradeType } from '@uniswap/sdk-core'
import {
  AlphaRouterConfig,
  CachingTokenListProvider,
  CurrencyAmount,
  GetQuotesResult,
  IGasModel,
  IOnChainGasModelFactory,
  ITokenProvider,
  IV3PoolProvider,
  IV3SubgraphProvider,
  NodeJSCache,
  RouteWithValidQuote,
  SwapRoute,
  V3RouteWithValidQuote,
  V3SubgraphProviderWithFallBacks,
} from '@uniswap/smart-order-router'
import { CandidatePoolsBySelectionCriteria } from '@uniswap/smart-order-router/build/main/routers/alpha-router/functions/get-candidate-pools'
import { buildTrade } from '@uniswap/smart-order-router/build/main/util/methodParameters'
import { DEFAULT_ROUTING_CONFIG_BY_CHAIN } from '@uniswap/smart-order-router/build/module/routers/alpha-router/config'
import { SwapOptions } from '@uniswap/v3-sdk'
import retry from 'async-retry'
import { SupportedChainId } from 'constants/chains'
import { TOKEN_LIST_BY_CHAIN } from 'constants/lists'
import DEFAULT_TOKEN_LIST from 'constants/tokenLists/tokenlist.json'
import { BigNumber } from 'ethers'
import _ from 'lodash'
import NodeCache from 'node-cache'
import store from 'state/index'

import { getBestSwapRoute } from './functions/best-swap-route'
import { V3HeuristicGasModelFactory } from './gas-models/v3-heuristic-gas-model'
import { CachingV3PoolProvider } from './providers/caching-pool-provider'
import { CachingTokenProviderWithFallback } from './providers/caching-token-provider'
import { UniswapMulticallProvider } from './providers/multicall-uniswap-provider'
import { OnChainQuoteProvider } from './providers/on-chain-quote-provider'
import { V3PoolProvider } from './providers/pool-provider'
import { StaticV3SubgraphProvider } from './providers/static-subgraph-provider'
import { TokenProvider } from './providers/token-provider'
import { V3Quoter } from './quoters/v3-quoter'

type BestSwapRoute = {
  quote: CurrencyAmount
  quoteGasAdjusted: CurrencyAmount
  estimatedGasUsed: BigNumber
  estimatedGasUsedUSD: CurrencyAmount
  estimatedGasUsedQuoteToken: CurrencyAmount
  routes: RouteWithValidQuote[]
}

interface AlphaRouterParams {
  chainId: SupportedChainId
  provider: BaseProvider
}

export class AlphaRouter {
  protected chainId: SupportedChainId
  protected provider: BaseProvider
  protected v3Quoter: V3Quoter
  protected v3GasModelFactory: IOnChainGasModelFactory
  protected v3PoolProvider: IV3PoolProvider
  protected multicall2Provider: UniswapMulticallProvider
  protected onChainQuoteProvider: OnChainQuoteProvider
  protected tokenProvider: ITokenProvider
  protected v3SubgraphProvider: IV3SubgraphProvider

  constructor({ chainId, provider }: AlphaRouterParams) {
    this.chainId = chainId
    this.provider = provider
    this.multicall2Provider = new UniswapMulticallProvider(chainId, provider, 375_000)

    this.onChainQuoteProvider = new OnChainQuoteProvider(
      chainId,
      provider,
      this.multicall2Provider,
      {
        retries: 2,
        minTimeout: 100,
        maxTimeout: 1000,
      },
      {
        multicallChunk: 210,
        gasLimitPerCall: 705_000,
        quoteMinSuccessRate: 0.15,
      },
      {
        gasLimitOverride: 2_000_000,
        multicallChunk: 70,
      }
    )
    this.v3PoolProvider = new CachingV3PoolProvider(
      this.chainId,
      new V3PoolProvider(chainId, this.multicall2Provider),
      new NodeJSCache(new NodeCache({ stdTTL: 360, useClones: false }))
    )
    this.v3SubgraphProvider = new V3SubgraphProviderWithFallBacks([
      new StaticV3SubgraphProvider(chainId, this.v3PoolProvider),
    ])

    const tokenList = store.getState().lists.byUrl[TOKEN_LIST_BY_CHAIN[this.chainId]].current ?? DEFAULT_TOKEN_LIST

    this.v3GasModelFactory = new V3HeuristicGasModelFactory()
    this.tokenProvider = new CachingTokenProviderWithFallback(
      chainId,
      new NodeJSCache(new NodeCache({ stdTTL: 3600, useClones: false })),
      new CachingTokenListProvider(
        chainId,
        tokenList,
        new NodeJSCache(new NodeCache({ stdTTL: 3600, useClones: false }))
      ),
      new TokenProvider(chainId, this.multicall2Provider)
    )
    this.v3Quoter = new V3Quoter(
      this.v3SubgraphProvider,
      //@ts-ignore
      this.v3PoolProvider,
      this.onChainQuoteProvider,
      this.tokenProvider,
      this.chainId
    )
  }

  public async route(
    amount: CurrencyAmount,
    quoteCurrency: Currency,
    tradeType: TradeType,
    swapConfig?: SwapOptions,
    partialRoutingConfig: Partial<AlphaRouterConfig> = {}
  ): Promise<SwapRoute | null> {
    const { currencyIn, currencyOut } = this.determineCurrencyInOutFromTradeType(tradeType, amount, quoteCurrency)

    const tokenIn = currencyIn.wrapped
    const tokenOut = currencyOut.wrapped

    // Get a block number to specify in all our calls. Ensures data we fetch from chain is
    // from the same block.
    const blockNumber = partialRoutingConfig.blockNumber ?? this.getBlockNumberPromise()

    const routingConfig: AlphaRouterConfig = _.merge(
      {},
      //@ts-ignore
      DEFAULT_ROUTING_CONFIG_BY_CHAIN(this.chainId),
      partialRoutingConfig,
      { blockNumber }
    )

    const gasPriceWei = await this.getGasPriceWei()

    const quoteToken = quoteCurrency.wrapped

    const [v3GasModel] = await this.getGasModels(gasPriceWei, amount.currency.wrapped, quoteToken)

    // Create a Set to sanitize the protocols input, a Set of undefined becomes an empty set,
    // Then create an Array from the values of that Set.
    const protocols: Protocol[] = Array.from(new Set(routingConfig.protocols).values())

    const swapRouteRaw = await this.getSwapRouteFromChain(
      amount,
      tokenIn,
      tokenOut,
      protocols,
      quoteToken,
      tradeType,
      routingConfig,
      v3GasModel
    )

    if (!swapRouteRaw) {
      return null
    }

    const {
      quote,
      quoteGasAdjusted,
      estimatedGasUsed,
      routes: routeAmounts,
      estimatedGasUsedQuoteToken,
      estimatedGasUsedUSD,
    } = swapRouteRaw

    // Build Trade object that represents the optimal swap.
    const trade = buildTrade<typeof tradeType>(currencyIn, currencyOut, tradeType, routeAmounts)

    const swapRoute: SwapRoute = {
      quote,
      quoteGasAdjusted,
      estimatedGasUsed,
      estimatedGasUsedQuoteToken,
      estimatedGasUsedUSD,
      gasPriceWei,
      route: routeAmounts,
      trade,
      blockNumber: BigNumber.from(await blockNumber),
    }

    return swapRoute
  }

  private determineCurrencyInOutFromTradeType(tradeType: TradeType, amount: CurrencyAmount, quoteCurrency: Currency) {
    if (tradeType === TradeType.EXACT_INPUT) {
      return {
        currencyIn: amount.currency,
        currencyOut: quoteCurrency,
      }
    } else {
      return {
        currencyIn: quoteCurrency,
        currencyOut: amount.currency,
      }
    }
  }

  private getBlockNumberPromise(): number | Promise<number> {
    return retry(
      async (_b, attempt) => {
        if (attempt > 1) {
          console.info(`Get block number attempt ${attempt}`)
        }
        return this.provider.getBlockNumber()
      },
      {
        retries: 2,
        minTimeout: 100,
        maxTimeout: 1000,
      }
    )
  }

  private async getGasPriceWei(): Promise<BigNumber> {
    return this.provider.getGasPrice()
  }

  private async getGasModels(
    gasPriceWei: BigNumber,
    amountToken: Token,
    quoteToken: Token
  ): Promise<[IGasModel<V3RouteWithValidQuote>]> {
    const v3GasModelPromise = this.v3GasModelFactory.buildGasModel({
      //@ts-ignore
      chainId: this.chainId,
      gasPriceWei,
      v3poolProvider: this.v3PoolProvider,
      amountToken,
      quoteToken,
    })

    const [v3GasModel] = await Promise.all([v3GasModelPromise])

    return [v3GasModel]
  }

  private async getSwapRouteFromChain(
    amount: CurrencyAmount,
    tokenIn: Token,
    tokenOut: Token,
    protocols: Protocol[],
    quoteToken: Token,
    tradeType: TradeType,
    routingConfig: AlphaRouterConfig,
    v3GasModel: IGasModel<V3RouteWithValidQuote>
  ): Promise<BestSwapRoute | null> {
    // Generate our distribution of amounts, i.e. fractions of the input amount.
    // We will get quotes for fractions of the input amount for different routes, then
    // combine to generate split routes.
    const [percents, amounts] = this.getAmountDistribution(amount, routingConfig)

    const noProtocolsSpecified = protocols.length === 0
    const v3ProtocolSpecified = protocols.includes(Protocol.V3)

    const quotePromises: Promise<GetQuotesResult>[] = []

    // Maybe Quote V3 - if V3 is specified, or no protocol is specified
    if (v3ProtocolSpecified || noProtocolsSpecified) {
      quotePromises.push(
        this.v3Quoter.getRoutesThenQuotes(
          tokenIn,
          tokenOut,
          amounts,
          percents,
          quoteToken,
          tradeType,
          routingConfig,
          v3GasModel
        )
      )
    }

    const getQuotesResults = await Promise.all(quotePromises)

    const allRoutesWithValidQuotes: RouteWithValidQuote[] = []
    const allCandidatePools: CandidatePoolsBySelectionCriteria[] = []
    getQuotesResults.forEach((getQuoteResult) => {
      allRoutesWithValidQuotes.push(...getQuoteResult.routesWithValidQuotes)
      if (getQuoteResult.candidatePools) {
        allCandidatePools.push(getQuoteResult.candidatePools)
      }
    })

    if (allRoutesWithValidQuotes.length === 0) {
      return null
    }

    // Given all the quotes for all the amounts for all the routes, find the best combination.
    const bestSwapRoute = await getBestSwapRoute(
      amount,
      percents,
      allRoutesWithValidQuotes,
      tradeType,
      //@ts-ignore
      this.chainId,
      routingConfig,
      v3GasModel
    )

    return bestSwapRoute
  }

  private getAmountDistribution(
    amount: CurrencyAmount,
    routingConfig: AlphaRouterConfig
  ): [number[], CurrencyAmount[]] {
    const { distributionPercent } = routingConfig
    const percents = []
    const amounts = []

    for (let i = 1; i <= 100 / distributionPercent; i++) {
      percents.push(i * distributionPercent)
      amounts.push(amount.multiply(new Fraction(i * distributionPercent, 100)))
    }

    return [percents, amounts]
  }
}
